import React, { useEffect, useState } from 'react';
import { CircularProgress } from '@mui/material';
import {
  QuestionAnswer as ResponseIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  Close as CloseIcon,
  Search as SearchIcon,
  Category as CategoryIcon,
  ContentCopy as CopyIcon
} from '@mui/icons-material';
import { Helmet } from 'react-helmet';
import cannedResponsesService from '../../services/cannedResponses.service';
import './TicketStyles.css';

interface CannedResponse {
  _id: string;
  title: string;
  content: string;
  category?: string;
  createdAt: string;
  updatedAt: string;
}

const CannedResponsesPage: React.FC = () => {
  const [items, setItems] = useState<CannedResponse[]>([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<CannedResponse | null>(null);
  const [form, setForm] = useState({ title: '', content: '', category: '' });
  const [copiedId, setCopiedId] = useState<string | null>(null);

  const load = async () => {
    setLoading(true);
    try {
      const data = await cannedResponsesService.list(search);
      setItems(data);
    } catch (error) {
      console.error('Error loading canned responses:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const onSearch = () => {
    load();
  };

  const onAdd = () => {
    setEditing(null);
    setForm({ title: '', content: '', category: '' });
    setDialogOpen(true);
  };

  const onEdit = (item: CannedResponse) => {
    setEditing(item);
    setForm({
      title: item.title,
      content: item.content,
      category: item.category || ''
    });
    setDialogOpen(true);
  };

  const onDelete = async (id: string) => {
    if (!window.confirm('آیا از حذف این پاسخ آماده اطمینان دارید؟')) return;
    try {
      await cannedResponsesService.remove(id);
      await load();
    } catch (error) {
      console.error('Error deleting canned response:', error);
    }
  };

  const onSubmit = async () => {
    try {
      if (editing) {
        await cannedResponsesService.update(editing._id, form);
      } else {
        await cannedResponsesService.create(form);
      }
      setDialogOpen(false);
      await load();
    } catch (error) {
      console.error('Error saving canned response:', error);
    }
  };

  const copyToClipboard = async (content: string, id: string) => {
    try {
      await navigator.clipboard.writeText(content);
      setCopiedId(id);
      setTimeout(() => setCopiedId(null), 2000);
    } catch (error) {
      console.error('Error copying to clipboard:', error);
    }
  };

  const categories = Array.from(new Set(items.filter(i => i.category).map(i => i.category)));

  return (
    <>
      <Helmet>
        <title>پاسخ‌های آماده</title>
      </Helmet>

      <div className="ticket-page">
        {/* Header */}
        <div className="ticket-header">
          <div className="ticket-header-title">
            <h1>
              <ResponseIcon />
              پاسخ‌های آماده
            </h1>
            <span className="ticket-header-subtitle">
              قالب‌های آماده برای پاسخ سریع به سوالات متداول کاربران
            </span>
          </div>

          <div className="header-actions">
            <button className="header-btn" onClick={load} disabled={loading}>
              <RefreshIcon style={{ fontSize: 18 }} />
              بروزرسانی
            </button>
            <button className="header-btn primary" onClick={onAdd}>
              <AddIcon style={{ fontSize: 18 }} />
              پاسخ جدید
            </button>
          </div>
        </div>

        {/* Search Section */}
        <div className="filters-section">
          <div className="filters-grid" style={{ gridTemplateColumns: 'repeat(3, 1fr)' }}>
            <div className="filter-group" style={{ gridColumn: 'span 2' }}>
              <label className="filter-label">جستجو در عنوان و محتوا</label>
              <div style={{ display: 'flex', gap: 8 }}>
                <input
                  type="text"
                  className="filter-input"
                  placeholder="عبارت مورد نظر را وارد کنید..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && onSearch()}
                />
                <button className="filter-btn primary" onClick={onSearch}>
                  <SearchIcon style={{ fontSize: 18 }} />
                  جستجو
                </button>
              </div>
            </div>

            {categories.length > 0 && (
              <div className="filter-group">
                <label className="filter-label">دسته‌بندی‌ها</label>
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                  {categories.map((cat) => (
                    <span
                      key={cat}
                      className="status-badge"
                      style={{
                        background: 'var(--color-primary-bg)',
                        color: 'var(--color-primary)',
                        cursor: 'pointer'
                      }}
                      onClick={() => setSearch(cat || '')}
                    >
                      <CategoryIcon style={{ fontSize: 12 }} />
                      {cat}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {loading ? (
          <div className="loading-overlay">
            <CircularProgress size={40} />
            <p>در حال بارگذاری...</p>
          </div>
        ) : items.length === 0 ? (
          <div className="empty-state">
            <ResponseIcon />
            <h3>پاسخ آماده‌ای یافت نشد</h3>
            <p>برای شروع یک پاسخ آماده جدید ایجاد کنید</p>
          </div>
        ) : (
          <div className="cards-grid">
            {items.map((item) => (
              <div key={item._id} className="item-card">
                <div className="item-card-header">
                  <h3 className="item-card-title">{item.title}</h3>
                  <div className="item-card-actions">
                    <button
                      className="row-action-btn"
                      onClick={() => copyToClipboard(item.content, item._id)}
                      title="کپی متن"
                      style={copiedId === item._id ? { borderColor: 'var(--color-success)', color: 'var(--color-success)', background: 'var(--color-success-bg)' } : {}}
                    >
                      <CopyIcon style={{ fontSize: 18 }} />
                    </button>
                    <button
                      className="row-action-btn"
                      onClick={() => onEdit(item)}
                      title="ویرایش"
                    >
                      <EditIcon style={{ fontSize: 18 }} />
                    </button>
                    <button
                      className="row-action-btn danger"
                      onClick={() => onDelete(item._id)}
                      title="حذف"
                    >
                      <DeleteIcon style={{ fontSize: 18 }} />
                    </button>
                  </div>
                </div>

                {item.category && (
                  <span
                    className="status-badge"
                    style={{
                      background: 'var(--color-primary-bg)',
                      color: 'var(--color-primary)',
                      marginBottom: 12,
                      display: 'inline-flex'
                    }}
                  >
                    <CategoryIcon style={{ fontSize: 12 }} />
                    {item.category}
                  </span>
                )}

                <p className="item-card-content" style={{
                  maxHeight: 100,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  display: '-webkit-box',
                  WebkitLineClamp: 4,
                  WebkitBoxOrient: 'vertical'
                }}>
                  {item.content}
                </p>

                <div className="item-card-meta">
                  <span>
                    آخرین بروزرسانی: {new Date(item.updatedAt).toLocaleDateString('fa-IR')}
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Create/Edit Dialog */}
        {dialogOpen && (
          <div className="dialog-overlay" onClick={() => setDialogOpen(false)}>
            <div className="dialog large" onClick={(e) => e.stopPropagation()}>
              <div className="dialog-header">
                <h2>
                  <ResponseIcon />
                  {editing ? 'ویرایش پاسخ آماده' : 'ایجاد پاسخ آماده جدید'}
                </h2>
                <button className="dialog-close" onClick={() => setDialogOpen(false)}>
                  <CloseIcon />
                </button>
              </div>

              <div className="dialog-content">
                <div className="form-group">
                  <label className="form-label">عنوان</label>
                  <input
                    type="text"
                    className="form-input"
                    value={form.title}
                    onChange={(e) => setForm({ ...form, title: e.target.value })}
                    placeholder="عنوان پاسخ آماده"
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">دسته‌بندی (اختیاری)</label>
                  <input
                    type="text"
                    className="form-input"
                    value={form.category}
                    onChange={(e) => setForm({ ...form, category: e.target.value })}
                    placeholder="مثال: پرداخت، ارسال، پشتیبانی"
                    list="categories-list"
                  />
                  <datalist id="categories-list">
                    {categories.map((cat) => (
                      <option key={cat} value={cat || ''} />
                    ))}
                  </datalist>
                </div>

                <div className="form-group">
                  <label className="form-label">متن پاسخ</label>
                  <textarea
                    className="form-textarea"
                    value={form.content}
                    onChange={(e) => setForm({ ...form, content: e.target.value })}
                    placeholder="متن کامل پاسخ آماده..."
                    rows={8}
                  />
                </div>
              </div>

              <div className="dialog-actions">
                <button className="filter-btn secondary" onClick={() => setDialogOpen(false)}>
                  انصراف
                </button>
                <button
                  className="filter-btn primary"
                  onClick={onSubmit}
                  disabled={!form.title.trim() || !form.content.trim()}
                >
                  ذخیره
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default CannedResponsesPage;
